import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { employeesAPI, documentsAPI, i983API } from '../../services/api';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { fmtDate } from '../../utils/dateUtils';
import WorkHistory from './WorkHistory';

/* ── Document configuration ─────────────────────────────────────────────────── */
const DOC_CONFIG = {
  ead_card: {
    label: 'EAD Card', icon: '💳',
    fields: [
      { key: 'document_number', label: 'EAD Number',      type: 'text', required: true },
      { key: 'start_date',      label: 'EAD Start Date',  type: 'date', required: true },
      { key: 'expiry_date',     label: 'EAD Expiry Date', type: 'date', required: true },
    ],
    hasFile: true, hasExpiry: true, supportsNA: false, noAlert: false,
  },
  driving_license: {
    label: 'Driving License', icon: '🪪',
    fields: [
      { key: 'document_number', label: 'License Number', type: 'text', required: true },
      { key: 'expiry_date',     label: 'Expiry Date',    type: 'date', required: true },
    ],
    hasFile: true, hasExpiry: true, supportsNA: false, noAlert: false,
  },
  passport: {
    label: 'Passport', icon: '📘',
    fields: [
      { key: 'document_number', label: 'Passport Number', type: 'text', required: true },
      { key: 'expiry_date',     label: 'Expiry Date',     type: 'date', required: true },
    ],
    hasFile: true, hasExpiry: true, supportsNA: false, noAlert: false,
  },
  ssn_card: {
    label: 'SSN Card', icon: '🔢',
    fields: [
      { key: 'document_number', label: 'Last 4 digits of SSN', type: 'text',
        required: true, maxLength: 4, placeholder: '1234' },
    ],
    hasFile: true, hasExpiry: false, supportsNA: false, noAlert: true,
  },
  visa_copy: {
    label: 'Visa Copy', icon: '✈️',
    fields: [
      { key: 'document_number', label: 'Visa Type',   type: 'text', required: true, placeholder: 'e.g. F-1' },
      { key: 'expiry_date',     label: 'Expiry Date', type: 'date', required: true },
    ],
    hasFile: true, hasExpiry: true, supportsNA: false, noAlert: false,
  },
  i20_certificate: {
    label: 'I-20 Certificate', icon: '🎓',
    fields: [],
    hasFile: true, hasExpiry: false, supportsNA: false, noAlert: true,
  },
  i94: {
    label: 'I-94', icon: '📋',
    fields: [],
    hasFile: true, hasExpiry: false, supportsNA: true, noAlert: true,
  },
  company_joining: {
    label: 'Company Joining', icon: '🏢',
    fields: [
      { key: 'expiry_date', label: 'Joining Date', type: 'date', required: true },
    ],
    hasFile: true, fileLabel: 'Offer Letter', hasExpiry: false, supportsNA: false, noAlert: true,
  },
  i9: {
    label: 'I-9', icon: '📄',
    fields: [
      { key: 'document_number', label: 'Case / Reference Number', type: 'text', required: false, placeholder: 'Optional' },
    ],
    hasFile: true, hasExpiry: false, supportsNA: false, noAlert: true,
  },
  everify: {
    label: 'E-Verify', icon: '✅',
    fields: [
      { key: 'document_number', label: 'E-Verify Case Number', type: 'text', required: false, placeholder: 'Optional' },
    ],
    hasFile: true, hasExpiry: false, supportsNA: false, noAlert: true,
  },
  i797: {
    label: 'I-797 (H-1B Approval)', icon: '🏢',
    fields: [
      { key: 'document_number', label: 'Receipt Number', type: 'text', required: true },
      { key: 'expiry_date',     label: 'Expiry Date',    type: 'date', required: true },
    ],
    hasFile: true, hasExpiry: true, supportsNA: false, noAlert: false,
  },
  h1b_ead: {
    label: 'H-1B EAD', icon: '💳',
    fields: [
      { key: 'document_number', label: 'EAD Number',    type: 'text', required: false, placeholder: 'Optional' },
      { key: 'expiry_date',     label: 'Expiry Date',   type: 'date', required: false },
    ],
    hasFile: true, hasExpiry: true, supportsNA: true, noAlert: false,
  },
};

const DOC_ORDER = ['ead_card','driving_license','passport','ssn_card','visa_copy','i20_certificate','i94','company_joining','i9','everify','i797','h1b_ead'];
const CAT_COLOR = {
  OPT:         'bg-blue-100 text-blue-700',
  STEM_OPT:    'bg-violet-100 text-violet-700',
  CPT:         'bg-green-100 text-green-700',
  H1B:         'bg-amber-100 text-amber-700',
  H4_EAD:      'bg-pink-100 text-pink-700',
  GC:          'bg-emerald-100 text-emerald-700',
  CITIZENSHIP: 'bg-teal-100 text-teal-700',
};
const CAT_LABEL = {
  OPT:'OPT', STEM_OPT:'STEM OPT', CPT:'CPT',
  H1B:'H-1B', H4_EAD:'H-4 EAD', GC:'Green Card', CITIZENSHIP:'Citizenship',
};

// All supported visa categories — employee can move to any of these at any time.
// OPT → STEM_OPT uses the dedicated two-step flow; all others use transition.php.
const ALL_VISA_CATEGORIES = ['OPT', 'STEM_OPT', 'CPT', 'H1B', 'H4_EAD', 'GC', 'CITIZENSHIP'];

const COUNTRY_CODES = [
  {code:'+1',label:'+1 (US/CA)'},{code:'+91',label:'+91 (India)'},{code:'+44',label:'+44 (UK)'},
  {code:'+61',label:'+61 (AU)'},{code:'+86',label:'+86 (China)'},{code:'+49',label:'+49 (Germany)'},
  {code:'+33',label:'+33 (France)'},{code:'+81',label:'+81 (Japan)'},{code:'+55',label:'+55 (Brazil)'},
  {code:'+52',label:'+52 (Mexico)'},
];

const I983_REASONS = [
  { value:'initial_stem_opt',  label:'Initial' },
  { value:'rate_change',       label:'Rate Change' },
  { value:'location_change',   label:'Location Change' },
  { value:'client_change',     label:'Client Change' },
  { value:'hours_change',      label:'Number of Working Hours Change' },
  { value:'6_month_review',    label:'6 Month Review' },
  { value:'12_month_review',   label:'12 Month Review' },
  { value:'18_month_review',   label:'18 Month Review' },
  { value:'24_month_review',   label:'24 Month Review' },
];

/* ══════════════════════════════════════════════════════════════════════════════
   Main component
══════════════════════════════════════════════════════════════════════════════ */
export default function EmployeeProfile() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [employee,    setEmployee]    = useState(null);
  const [docs,        setDocs]        = useState({});
  const [visaHistory, setVisaHistory] = useState([]);
  const [i983List,    setI983List]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');

  const [activeTab,   setActiveTab]   = useState('documents'); // 'documents' | 'work_history'

  const [editMode,    setEditMode]    = useState(false);
  const [editForm,    setEditForm]    = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError,   setEditError]   = useState('');

  function loadData() {
    setLoading(true);
    Promise.all([
      employeesAPI.get(id),
      documentsAPI.list(id),
      employeesAPI.visaHistory(id),
      i983API.list(id),
    ])
      .then(([empRes, docRes, histRes, i983Res]) => {
        const emp = empRes.data.data.employee;
        setEmployee(emp);
        setDocs(docRes.data.data.documents);
        setVisaHistory(histRes.data.data.history || []);
        setI983List(i983Res.data.data.documents || []);
        initEditForm(emp);
      })
      .catch(() => setError('Failed to load employee.'))
      .finally(() => setLoading(false));
  }

  function initEditForm(emp) {
    setEditForm({
      first_name:      emp.first_name      || '',
      last_name:       emp.last_name       || '',
      date_of_birth:   emp.date_of_birth   || '',
      email:           emp.email           || '',
      country_code:    emp.country_code    || '+1',
      phone:           emp.phone           || '',
      current_address: emp.current_address || '',
      category:        emp.category        || 'OPT',
      uscis_number:    emp.uscis_number    || '',
      ead_expiry_date: emp.ead_expiry_date || '',
    });
  }

  useEffect(() => { loadData(); }, [id]); // eslint-disable-line

  function handleDocSaved(docType, updatedGroup) {
    setDocs((prev) => ({ ...prev, [docType]: updatedGroup }));
  }

  function startEdit() { initEditForm(employee); setEditError(''); setEditMode(true); }
  function cancelEdit() { setEditMode(false); setEditError(''); }
  function setField(key, value) { setEditForm((p) => ({ ...p, [key]: value })); }

  async function saveEdit(e) {
    e.preventDefault();
    setEditError('');
    const phone = editForm.phone.replace(/\D/g, '');
    if (phone.length !== 10) { setEditError('Phone must be exactly 10 digits.'); return; }
    setEditLoading(true);
    try {
      await employeesAPI.update({ id: Number(id), ...editForm, phone });
      const res = await employeesAPI.get(id);
      const updated = res.data.data.employee;
      setEmployee(updated);
      initEditForm(updated);
      setEditMode(false);
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to save changes.');
    } finally {
      setEditLoading(false);
    }
  }

  if (loading) return <LoadingSpinner fullPage />;
  if (error)   return <div className="text-center py-16 text-red-600">{error}</div>;

  const uploadedCount = DOC_ORDER.filter((t) => {
    const d = docs[t];
    return d?.current?.status === 'uploaded' || d?.current?.is_not_applicable;
  }).length;
  const pct        = Math.round((uploadedCount / DOC_ORDER.length) * 100);
  const hasExpired = DOC_ORDER.some((t) => !DOC_CONFIG[t].noAlert && docs[t]?.current?.expiry_status === 'expired');

  const eadDaysLeft = employee.ead_expiry_date
    ? Math.floor((new Date(employee.ead_expiry_date) - new Date()) / 86400000)
    : null;
  // All categories except the current one are available as transition targets
  const availableTransitions = ALL_VISA_CATEGORIES.filter((c) => c !== employee.category);

  return (
    <div className="space-y-5 max-w-4xl mx-auto">

      {/* ── Profile card ──────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        {editMode ? (
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-slate-800 text-base">Edit Employee Details</h3>
              <button type="button" onClick={cancelEdit} className="text-slate-400 hover:text-slate-600 text-sm">✕ Cancel</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="First Name" required><input value={editForm.first_name} onChange={(e)=>setField('first_name',e.target.value)} className="input-base" required /></Field>
              <Field label="Last Name" required><input value={editForm.last_name} onChange={(e)=>setField('last_name',e.target.value)} className="input-base" required /></Field>
              <Field label="Date of Birth"><input type="date" value={editForm.date_of_birth} max={new Date().toISOString().split('T')[0]} onChange={(e)=>setField('date_of_birth',e.target.value)} className="input-base" /></Field>
              <Field label="Email" required><input type="email" value={editForm.email} onChange={(e)=>setField('email',e.target.value)} className="input-base" required /></Field>
              <Field label="Phone" required>
                <div className="flex gap-2">
                  <select value={editForm.country_code} onChange={(e)=>setField('country_code',e.target.value)} className="input-base w-36 shrink-0">
                    {COUNTRY_CODES.map((c)=><option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                  <input value={editForm.phone} onChange={(e)=>setField('phone',e.target.value.replace(/\D/g,'').slice(0,10))} placeholder="10 digits" className="input-base flex-1" required />
                </div>
              </Field>
              <Field label="Category" required>
                <select value={editForm.category} onChange={(e)=>setField('category',e.target.value)} className="input-base">
                  <option value="OPT">OPT</option>
                  <option value="STEM_OPT">STEM OPT</option>
                  <option value="CPT">CPT</option>
                </select>
              </Field>
              <Field label="USCIS Number" required><input value={editForm.uscis_number} onChange={(e)=>setField('uscis_number',e.target.value)} className="input-base" required /></Field>
              <Field label="EAD Expiry Date" required><input type="date" value={editForm.ead_expiry_date} onChange={(e)=>setField('ead_expiry_date',e.target.value)} className="input-base" required /></Field>
            </div>
            <Field label="Current Address"><textarea value={editForm.current_address} onChange={(e)=>setField('current_address',e.target.value)} rows={2} className="input-base resize-none" /></Field>
            {editError && <p className="text-red-600 text-sm bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{editError}</p>}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={cancelEdit} className="flex-1 border border-slate-300 text-slate-600 py-2.5 rounded-lg text-sm hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={editLoading} className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
                {editLoading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner"/>Saving…</> : 'Save Changes'}
              </button>
            </div>
          </form>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center text-xl font-bold text-blue-700 shrink-0">
              {employee.first_name[0]}{employee.last_name[0]}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-bold text-slate-800">{employee.first_name} {employee.last_name}</h2>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${CAT_COLOR[employee.category]}`}>
                  {CAT_LABEL[employee.category]}
                </span>
                {employee.category === 'OPT' && employee.stem_application_status === 'applied' && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                    STEM Applied
                  </span>
                )}
                {employee.category === 'STEM_OPT' && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                    Active
                  </span>
                )}
                {hasExpired && <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">⚠ Expired Docs</span>}
              </div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm text-slate-600">
                <Info icon="✉" v={employee.email} />
                <Info icon="📱" v={`${employee.country_code||''} ${employee.phone}`} />
                {employee.date_of_birth   && <Info icon="🎂" v={`DOB: ${fmtDate(employee.date_of_birth)}`} />}
                {employee.current_address && <Info icon="📍" v={employee.current_address} />}
                <Info icon="🪪" v={`USCIS: ${employee.uscis_number}`} />
                <Info icon="📅" v={`EAD Expires: ${fmtDate(employee.ead_expiry_date)}`} />
                {employee.category === 'STEM_OPT' && employee.stem_uscis_number && (
                  <Info icon="🎓" v={`STEM USCIS: ${employee.stem_uscis_number}`} />
                )}
                {employee.category === 'STEM_OPT' && employee.stem_expiry_date && (
                  <Info icon="📋" v={`STEM Expires: ${fmtDate(employee.stem_expiry_date)}`} />
                )}
                {docs.company_joining?.current?.expiry_date && docs.company_joining.current.status === 'uploaded' && (
                  <Info icon="🏢" v={`Joined: ${fmtDate(docs.company_joining.current.expiry_date)}`} />
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button onClick={startEdit} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium">Edit</button>
              <button onClick={() => navigate('/employees')} className="text-slate-500 hover:text-slate-700 text-sm text-center">← Back</button>
            </div>
          </div>
        )}

        {!editMode && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium text-slate-700">Document Completion</span>
              <span className="text-sm font-bold text-slate-800">{uploadedCount}/{DOC_ORDER.length} ({pct}%)</span>
            </div>
            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full progress-bar ${pct===100?'bg-green-500':pct>=50?'bg-yellow-500':'bg-red-400'}`} style={{width:`${pct}%`}} />
            </div>
          </div>
        )}
      </div>

      {/* ── Tab chips ─────────────────────────────────────────────────────────── */}
      {!editMode && (
        <div className="flex gap-2">
          {[
            { key: 'documents',    label: 'Documents' },
            { key: 'work_history', label: 'Work History' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all border ${
                activeTab === tab.key
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400 hover:text-blue-600'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Documents tab ─────────────────────────────────────────────────────── */}
      {!editMode && activeTab === 'documents' && (
        <>
          {/* Visa timeline */}
          {visaHistory.length > 0 && (
            <VisaTimeline history={visaHistory} />
          )}

          {/* Visa transition section — shown for any category that has allowed next steps */}
          {availableTransitions.length > 0 && (
            <VisaTransitionSection
              employee={employee}
              eadDaysLeft={eadDaysLeft}
              employeeId={Number(id)}
              i983List={i983List}
              visaHistory={visaHistory}
              availableTransitions={availableTransitions}
              onChanged={() => loadData()}
            />
          )}

          {/* I-983 manager for STEM OPT employees */}
          {employee.category === 'STEM_OPT' && (
            <I983Manager
              employeeId={Number(id)}
              i983List={i983List}
              onUploaded={() => loadData()}
            />
          )}

          {/* Document cards */}
          <div>
            <h3 className="font-semibold text-slate-700 mb-3">Documents</h3>
            <div className="space-y-3">
              {DOC_ORDER.map((docType) => (
                <DocCard
                  key={docType}
                  employeeId={Number(id)}
                  docType={docType}
                  config={DOC_CONFIG[docType]}
                  grouped={docs[docType] || { current: null, history: [] }}
                  employee={employee}
                  onSaved={(updated) => handleDocSaved(docType, updated)}
                />
              ))}
            </div>
          </div>
          {uploadedCount === DOC_ORDER.length && (
            <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-4 text-center">
              🎉 <span className="font-semibold">All documents complete!</span>
            </div>
          )}
        </>
      )}

      {/* ── Work History tab ──────────────────────────────────────────────────── */}
      {!editMode && activeTab === 'work_history' && (
        <WorkHistory employeeId={Number(id)} employee={employee} />
      )}

    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Visa Timeline
══════════════════════════════════════════════════════════════════════════════ */
function VisaTimeline({ history }) {
  const CAT_STYLE = {
    OPT:         { bg:'bg-blue-100',    text:'text-blue-700',    dot:'bg-blue-500'    },
    STEM_OPT:    { bg:'bg-violet-100',  text:'text-violet-700',  dot:'bg-violet-500'  },
    CPT:         { bg:'bg-green-100',   text:'text-green-700',   dot:'bg-green-500'   },
    H1B:         { bg:'bg-amber-100',   text:'text-amber-700',   dot:'bg-amber-500'   },
    H4_EAD:      { bg:'bg-pink-100',    text:'text-pink-700',    dot:'bg-pink-500'    },
    GC:          { bg:'bg-emerald-100', text:'text-emerald-700', dot:'bg-emerald-500' },
    CITIZENSHIP: { bg:'bg-teal-100',    text:'text-teal-700',    dot:'bg-teal-500'    },
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="font-semibold text-slate-700 mb-4">Visa Status Timeline</h3>
      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
        <div className="space-y-3">
          {history.map((h, i) => {
            const s = CAT_STYLE[h.category] || CAT_STYLE.OPT;
            return (
              <div key={h.id} className="flex items-start gap-4 pl-10 relative">
                <div className={`absolute left-3 top-1.5 w-2.5 h-2.5 rounded-full ${s.dot} ring-2 ring-white`} />
                <div className={`flex-1 rounded-xl border px-4 py-3 ${s.bg} border-opacity-50`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className={`font-semibold text-sm ${s.text}`}>
                      {h.category.replace('_',' ')}
                      {h.is_current && <span className="ml-2 text-xs font-normal opacity-70">(Current)</span>}
                    </span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full bg-white/60 ${s.text}`}>
                      {h.days} day{h.days !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <p className="text-xs mt-1 opacity-70">
                    {fmtDate(h.start_date)} → {h.end_date ? fmtDate(h.end_date) : h.expiry_date ? fmtDate(h.expiry_date) : 'Present'}
                  </p>
                  {h.notes && <p className="text-xs mt-1 opacity-60 italic">{h.notes}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Visa Transition Section — dynamic next-stage selector for any category
══════════════════════════════════════════════════════════════════════════════ */
function VisaTransitionSection({ employee, eadDaysLeft, employeeId, i983List, visaHistory, availableTransitions, onChanged }) {
  const [selectedTarget,  setSelectedTarget]  = useState(null);
  const [revertConfirm,   setRevertConfirm]   = useState(false);
  const [revertLoading,   setRevertLoading]   = useState(false);
  const [revertErr,       setRevertErr]       = useState('');
  const [showOverride,    setShowOverride]    = useState(false);

  const isStemApplied = employee.category === 'OPT' && employee.stem_application_status === 'applied';

  // History is sorted ASC — last closed row is the most recently closed (the previous status)
  const closedRows   = (visaHistory || []).filter((h) => h.end_date);
  const prevCategory = closedRows.length > 0
    ? closedRows[closedRows.length - 1].category
    : null;

  async function handleRevert() {
    setRevertLoading(true);
    setRevertErr('');
    try {
      await employeesAPI.revertTransition({ employee_id: employeeId });
      setRevertConfirm(false);
      onChanged();
    } catch (ex) {
      setRevertErr(ex.response?.data?.message || 'Revert failed. Please try again.');
    } finally {
      setRevertLoading(false);
    }
  }

  // OPT pending STEM application — show approval form with override escape hatch
  if (isStemApplied && !showOverride) {
    return (
      <div className="space-y-3">
        <StemPendingApproval
          employee={employee}
          employeeId={employeeId}
          i983List={i983List}
          onChanged={onChanged}
        />
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-700">Need to change to a different stage?</p>
            <p className="text-xs text-slate-400 mt-0.5">Override the pending STEM OPT application and move to any visa category.</p>
          </div>
          <button
            onClick={() => { setShowOverride(true); setSelectedTarget(null); setRevertErr(''); }}
            className="shrink-0 px-4 py-2 text-sm font-semibold text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 whitespace-nowrap">
            Change Stage
          </button>
        </div>
      </div>
    );
  }

  const TARGET_META = {
    OPT:         { label:'OPT',         icon:'🎓', color:'blue'   },
    STEM_OPT:    { label:'STEM OPT',    icon:'🔬', color:'violet' },
    CPT:         { label:'CPT',         icon:'📋', color:'green'  },
    H1B:         { label:'H-1B',        icon:'🏢', color:'amber'  },
    H4_EAD:      { label:'H-4 EAD',     icon:'💼', color:'pink'   },
    GC:          { label:'Green Card',  icon:'🌿', color:'emerald'},
    CITIZENSHIP: { label:'Citizenship', icon:'🗽', color:'teal'   },
  };

  const BTN = {
    blue:    'border-blue-300    bg-blue-50    text-blue-700    hover:bg-blue-100',
    violet:  'border-violet-300  bg-violet-50  text-violet-700  hover:bg-violet-100',
    amber:   'border-amber-300   bg-amber-50   text-amber-700   hover:bg-amber-100',
    green:   'border-green-300   bg-green-50   text-green-700   hover:bg-green-100',
    pink:    'border-pink-300    bg-pink-50    text-pink-700    hover:bg-pink-100',
    emerald: 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
    teal:    'border-teal-300    bg-teal-50    text-teal-700    hover:bg-teal-100',
  };
  const ACTIVE = {
    blue:    'border-blue-500    bg-blue-600    text-white',
    violet:  'border-violet-500  bg-violet-600  text-white',
    amber:   'border-amber-500   bg-amber-600   text-white',
    green:   'border-green-500   bg-green-600   text-white',
    pink:    'border-pink-500    bg-pink-600    text-white',
    emerald: 'border-emerald-500 bg-emerald-600 text-white',
    teal:    'border-teal-500    bg-teal-600    text-white',
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-slate-800">Visa Transition</h3>
        {/* Revert button — only when there is a previous status to go back to */}
        {prevCategory && (
          <button
            onClick={() => { setRevertConfirm(true); setSelectedTarget(null); setRevertErr(''); }}
            className="flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700 border border-red-200 hover:border-red-400 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-all">
            ↩ Revert to {CAT_LABEL[prevCategory] || prevCategory}
          </button>
        )}
      </div>

      {/* Override warning — shown when bypassing a pending STEM OPT application */}
      {isStemApplied && (
        <div className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2.5">
          <span className="text-orange-500 shrink-0">⚠️</span>
          <p className="text-xs text-orange-700 flex-1">
            Override mode — transitioning will cancel the pending STEM OPT application.
          </p>
          <button
            onClick={() => { setShowOverride(false); setSelectedTarget(null); }}
            className="text-xs font-semibold text-slate-600 border border-slate-300 bg-white px-2.5 py-1 rounded-lg hover:bg-slate-50 shrink-0 whitespace-nowrap">
            ← Back to STEM App
          </button>
        </div>
      )}

      {/* Revert confirmation panel */}
      {revertConfirm && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-red-700">
            ⚠️ Revert visa status?
          </p>
          <p className="text-sm text-red-600">
            This will remove the current <strong>{CAT_LABEL[employee.category] || employee.category}</strong> status
            and restore <strong>{employee.first_name}</strong> to{' '}
            <strong>{CAT_LABEL[prevCategory] || prevCategory}</strong>.
            All transition data for the current status will be cleared.
          </p>
          {revertErr && (
            <p className="text-sm text-red-700 bg-red-100 border border-red-300 px-3 py-2 rounded-lg">{revertErr}</p>
          )}
          <div className="flex gap-3">
            <button onClick={handleRevert} disabled={revertLoading}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white text-sm font-semibold rounded-lg flex items-center gap-2">
              {revertLoading
                ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Reverting…</>
                : '↩ Confirm Revert'}
            </button>
            <button onClick={() => { setRevertConfirm(false); setRevertErr(''); }}
              className="px-4 py-2 border border-slate-300 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {!revertConfirm && (
        <>
          <p className="text-sm text-slate-500">
            {isStemApplied
              ? `Select any stage to override for ${employee.first_name}:`
              : `Select the next visa stage for ${employee.first_name}:`}
          </p>
          <div className="flex flex-wrap gap-2">
            {availableTransitions.map((t) => {
              const meta  = TARGET_META[t] || { label: t, icon: '📄', color: 'violet' };
              const color = meta.color;
              const isActive = selectedTarget === t;
              return (
                <button key={t}
                  onClick={() => setSelectedTarget(isActive ? null : t)}
                  className={`px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all
                    ${isActive ? ACTIVE[color] : BTN[color]}`}>
                  {meta.icon} {meta.label}
                </button>
              );
            })}
          </div>

          {selectedTarget === 'STEM_OPT' && (
            <StemApplyPrompt
              employee={employee}
              eadDaysLeft={eadDaysLeft}
              employeeId={employeeId}
              onApplied={onChanged}
            />
          )}
          {selectedTarget && selectedTarget !== 'STEM_OPT' && (
            <VisaTransitionForm
              employee={employee}
              employeeId={employeeId}
              targetCategory={selectedTarget}
              onDone={onChanged}
            />
          )}
        </>
      )}
    </div>
  );
}

/* ── Stage 0: Ask if they applied, then submit application ─────────────────── */
function StemApplyPrompt({ employee, eadDaysLeft, employeeId, onApplied }) {
  const [answer,    setAnswer]    = useState(null);
  const [notes,     setNotes]     = useState('');
  const [i983File,  setI983File]  = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [err,       setErr]       = useState('');
  const i983Ref = useRef();

  const urgencyBorder =
    eadDaysLeft !== null && eadDaysLeft < 0   ? 'border-red-300    bg-red-50' :
    eadDaysLeft !== null && eadDaysLeft <= 30  ? 'border-orange-300 bg-orange-50' :
    eadDaysLeft !== null && eadDaysLeft <= 90  ? 'border-yellow-300 bg-yellow-50' :
                                                 'border-blue-200   bg-blue-50';
  const urgencyText =
    eadDaysLeft === null           ? null :
    eadDaysLeft < 0                ? `EAD expired ${Math.abs(eadDaysLeft)} days ago` :
    eadDaysLeft === 0              ? 'EAD expires today' :
                                     `EAD expiring in ${eadDaysLeft} day${eadDaysLeft !== 1 ? 's' : ''}`;

  async function handleApply(e) {
    e.preventDefault();
    if (!i983File) { setErr('Please select the initial I-983 document.'); return; }
    setErr('');
    setLoading(true);
    try {
      // 1. Mark application as submitted (status → applied)
      await employeesAPI.applyStem({ employee_id: employeeId, notes });
      // 2. Upload initial I-983 (employee is now 'applied', which upload.php accepts)
      const fd = new FormData();
      fd.append('employee_id', String(employeeId));
      fd.append('reason',      'initial_stem_opt');
      fd.append('i983_file',   i983File);
      if (notes) fd.append('notes', notes);
      await i983API.upload(fd);
      onApplied();
    } catch (ex) {
      setErr(ex.response?.data?.message || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={`rounded-xl border-2 p-5 ${urgencyBorder}`}>
      <div className="flex items-start gap-3 mb-4">
        <span className="text-2xl">🎓</span>
        <div>
          <h3 className="font-bold text-slate-800">STEM OPT Transition</h3>
          {urgencyText && (
            <p className="text-sm text-slate-600 mt-0.5">
              {urgencyText} — {fmtDate(employee.ead_expiry_date)}
            </p>
          )}
          <p className="text-xs text-slate-500 mt-1">
            OPT EAD: {fmtDate(employee.ead_expiry_date)}
          </p>
        </div>
      </div>

      <p className="text-sm font-semibold text-slate-700 mb-3">
        Did {employee.first_name} apply for STEM OPT?
      </p>

      <div className="flex gap-3 mb-4">
        <button onClick={() => { setAnswer('yes'); setErr(''); }}
          className={`px-5 py-2 rounded-lg text-sm font-semibold border-2 transition-all
            ${answer === 'yes' ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-blue-400'}`}>
          Yes
        </button>
        <button onClick={() => setAnswer('no')}
          className={`px-5 py-2 rounded-lg text-sm font-semibold border-2 transition-all
            ${answer === 'no' ? 'bg-slate-600 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400'}`}>
          No
        </button>
      </div>

      {answer === 'no' && (
        <p className="text-sm text-slate-500 italic">
          No action taken. Update when they apply.
        </p>
      )}

      {answer === 'yes' && (
        <form onSubmit={handleApply} className="space-y-3 border-t border-white/60 pt-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
            Submit STEM OPT Application
          </p>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Initial I-983 Document <span className="text-red-500">*</span>
            </label>
            <div
              className="border border-dashed border-violet-300 rounded-lg p-3 text-center cursor-pointer bg-violet-50/40 hover:bg-violet-50 transition-colors"
              onClick={() => i983Ref.current?.click()}>
              {i983File
                ? <span className="text-sm text-blue-600 font-medium">{i983File.name}</span>
                : <span className="text-sm text-slate-400">Click to upload I-983 document (PDF / JPG / PNG)</span>}
            </div>
            <input ref={i983Ref} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
              onChange={(e) => {
                const f = e.target.files[0] || null;
                if (f && f.size > 20 * 1024 * 1024) { setErr(`File too large (${(f.size/1024/1024).toFixed(1)} MB). Max 20 MB.`); e.target.value = ''; }
                else { setI983File(f); setErr(''); }
              }} />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes (optional)</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Application filed on 15 May 2025"
              className="input-base" />
          </div>

          {err && <p className="text-red-600 text-sm bg-red-100 border border-red-200 px-3 py-2 rounded-lg">{err}</p>}
          <button type="submit" disabled={loading}
            className="w-full bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
            {loading
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Submitting…</>
              : '✓ Submit Application & Upload I-983'}
          </button>
          <p className="text-xs text-slate-400 text-center">
            Status becomes pending USCIS approval. Fill in approval details once USCIS approves.
          </p>
        </form>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Generic Visa Transition Form (H1B / CPT / H4_EAD / GC)
══════════════════════════════════════════════════════════════════════════════ */
function VisaTransitionForm({ employee, employeeId, targetCategory, onDone }) {
  const [form,      setForm]      = useState({});
  const [i797File,  setI797File]  = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [err,       setErr]       = useState('');
  const i797Ref = useRef();

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const FIELDS = {
    H1B: [
      { key:'h1b_start_date',  label:'H-1B Start Date',        type:'date', required:true  },
      { key:'h1b_i797_expiry', label:'I-797 Expiry Date',       type:'date', required:true  },
      { key:'h1b_i797_number', label:'I-797 Receipt Number',    type:'text', required:false },
      { key:'h1b_i94_number',  label:'I-94 Number',             type:'text', required:true  },
      { key:'h1b_ead_expiry',  label:'H-1B EAD Expiry (opt)',   type:'date', required:false },
    ],
    CPT: [
      { key:'cpt_start_date',  label:'CPT Start Date',          type:'date', required:true  },
      { key:'cpt_ead_expiry',  label:'CPT EAD Expiry Date',     type:'date', required:true  },
    ],
    H4_EAD: [
      { key:'h4_start_date',   label:'H-4 EAD Start Date',      type:'date', required:true  },
      { key:'h4_ead_expiry',   label:'H-4 EAD Expiry Date',     type:'date', required:true  },
    ],
    GC: [
      { key:'gc_receipt_date', label:'GC Receipt/Approval Date', type:'date', required:true },
    ],
  };

  const fields = FIELDS[targetCategory] || [];

  async function handleSubmit(e) {
    e.preventDefault();
    setErr('');
    for (const f of fields) {
      if (f.required && !form[f.key]?.trim()) {
        setErr(`${f.label} is required.`); return;
      }
    }
    setLoading(true);
    try {
      // H1B always uses FormData so the optional I-797 file can be included
      if (targetCategory === 'H1B') {
        const fd = new FormData();
        fd.append('employee_id',     String(employeeId));
        fd.append('target_category', 'H1B');
        fd.append('notes',           form.notes || '');
        fields.forEach((f) => fd.append(f.key, form[f.key] || ''));
        if (i797File) fd.append('i797_file', i797File);
        await employeesAPI.transition(fd);
      } else {
        await employeesAPI.transition({
          employee_id:     employeeId,
          target_category: targetCategory,
          notes:           form.notes || '',
          ...Object.fromEntries(fields.map((f) => [f.key, form[f.key] || ''])),
        });
      }
      onDone();
    } catch (ex) {
      setErr(ex.response?.data?.message || 'Transition failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const COLOR = { H1B:'amber', CPT:'green', H4_EAD:'pink', GC:'emerald' };
  const c = COLOR[targetCategory] || 'blue';
  const btnCls = `w-full bg-${c}-600 hover:bg-${c}-700 disabled:bg-${c}-400 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2`;

  return (
    <form onSubmit={handleSubmit} className="space-y-3 border-t border-slate-100 pt-4">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
        Transition Details
      </p>
      {fields.map((f) => (
        <div key={f.key}>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            {f.label} {f.required && <span className="text-red-500">*</span>}
          </label>
          <input
            type={f.type}
            value={form[f.key] || ''}
            onChange={(e) => set(f.key, e.target.value)}
            className="input-base"
          />
        </div>
      ))}

      {/* I-797 file upload — H1B only */}
      {targetCategory === 'H1B' && (
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            I-797 Approval Notice <span className="text-slate-400">(optional)</span>
          </label>
          <div
            onClick={() => i797Ref.current?.click()}
            className="border border-dashed border-amber-300 rounded-lg p-3 text-center cursor-pointer bg-amber-50/40 hover:bg-amber-50 transition-colors">
            {i797File
              ? <span className="text-sm text-amber-700 font-medium">{i797File.name} ({(i797File.size/1024/1024).toFixed(1)} MB)</span>
              : <span className="text-sm text-slate-400">Click to upload I-797 (PDF / JPG / PNG, max 20 MB)</span>}
          </div>
          <input ref={i797Ref} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
            onChange={(e) => {
              const f = e.target.files[0] || null;
              if (f && f.size > 20 * 1024 * 1024) {
                setErr(`File too large (${(f.size/1024/1024).toFixed(1)} MB). Max 20 MB.`);
                e.target.value = '';
              } else {
                setI797File(f);
                setErr('');
              }
            }} />
        </div>
      )}
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Notes (optional)</label>
        <input value={form.notes || ''} onChange={(e) => set('notes', e.target.value)}
          className="input-base" placeholder="e.g. H-1B approved, start date confirmed" />
      </div>
      {err && <p className="text-red-600 text-sm bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{err}</p>}
      <button type="submit" disabled={loading} className={btnCls}>
        {loading
          ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Processing…</>
          : `✓ Confirm Transition to ${targetCategory.replace('_',' ')}`}
      </button>
    </form>
  );
}

/* ── Stage 1: Application submitted — upload I-983 + enter approval details ── */
function StemPendingApproval({ employee, employeeId, i983List, onChanged }) {
  const [showApproveForm, setShowApproveForm] = useState(false);
  const [approveForm, setApproveForm] = useState({
    stem_uscis_number: '',
    stem_start_date: '', stem_expiry_date: '', stem_notes: '',
  });
  const [deleteI983Id,      setDeleteI983Id]      = useState(null);
  const [deleteI983Loading, setDeleteI983Loading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveErr,     setApproveErr]     = useState('');

  const [i983File,    setI983File]    = useState(null);
  const [i983Notes,   setI983Notes]   = useState('');
  const [i983Loading, setI983Loading] = useState(false);
  const [i983Err,     setI983Err]     = useState('');
  const [i983Success, setI983Success] = useState('');
  const i983Ref = useRef();

  const initialDocs = i983List.filter((d) => d.reason === 'initial_stem_opt');
  const sf = (k, v) => setApproveForm((p) => ({ ...p, [k]: v }));

  async function deleteI983(id) {
    setDeleteI983Loading(true);
    try {
      await i983API.delete(id);
      setDeleteI983Id(null);
      onChanged();
    } catch (ex) {
      alert(ex.response?.data?.message || 'Delete failed.');
    } finally {
      setDeleteI983Loading(false);
    }
  }

  async function uploadI983(e) {
    e.preventDefault();
    if (!i983File) { setI983Err('Please select a file.'); return; }
    setI983Err(''); setI983Success('');
    setI983Loading(true);
    try {
      const fd = new FormData();
      fd.append('employee_id', String(employeeId));
      fd.append('reason',      'initial_stem_opt');
      fd.append('i983_file',   i983File);
      if (i983Notes) fd.append('notes', i983Notes);
      await i983API.upload(fd);
      setI983Success('I-983 uploaded!');
      setI983File(null);
      if (i983Ref.current) i983Ref.current.value = '';
      setTimeout(() => { setI983Success(''); onChanged(); }, 1500);
    } catch (ex) {
      setI983Err(ex.response?.data?.message || 'Upload failed.');
    } finally {
      setI983Loading(false);
    }
  }

  async function handleApprove(e) {
    e.preventDefault();
    setApproveErr('');
    setApproveLoading(true);
    try {
      const fd = new FormData();
      fd.append('employee_id',      String(employeeId));
      fd.append('stem_uscis_number', approveForm.stem_uscis_number);
      fd.append('stem_start_date',   approveForm.stem_start_date);
      fd.append('stem_expiry_date',  approveForm.stem_expiry_date);
      fd.append('stem_notes',        approveForm.stem_notes || '');
      await employeesAPI.approveStem(fd);
      onChanged();
    } catch (ex) {
      setApproveErr(ex.response?.data?.message || 'Approval failed. Please try again.');
    } finally {
      setApproveLoading(false);
    }
  }

  return (
    <div className="rounded-xl border-2 border-violet-300 bg-violet-50 overflow-hidden">
      {/* Status header */}
      <div className="px-5 py-4 flex items-start gap-3">
        <span className="text-2xl mt-0.5">📋</span>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-bold text-violet-800">STEM OPT Application Submitted</h3>
            <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 text-xs font-semibold">
              Pending USCIS Approval
            </span>
          </div>
          <p className="text-xs text-violet-600 mt-0.5">
            Upload I-983 training plan now. Enter approval details once USCIS approves.
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            OPT EAD: {fmtDate(employee.ead_expiry_date)} &nbsp;·&nbsp; USCIS: {employee.uscis_number}
          </p>
        </div>
      </div>

      {/* ── I-983 upload ── */}
      <div className="border-t border-violet-200 px-5 py-4 bg-white space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-slate-700 text-sm">I-983 Documents</h4>
          {initialDocs.length > 0 && (
            <span className="text-xs text-green-600 font-semibold bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
              ✓ {initialDocs.length} uploaded
            </span>
          )}
        </div>

        {initialDocs.length > 0 && (
          <div className="space-y-1.5">
            {initialDocs.map((doc) => (
              <div key={doc.id}>
                <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm">
                  <span className="text-green-600 shrink-0">✓</span>
                  <span className="flex-1 text-slate-700 truncate text-xs">{doc.file_name}</span>
                  <span className="text-xs text-slate-400 shrink-0">{fmtDate(doc.created_at)}</span>
                  <a href={i983API.previewUrl(doc)} target="_blank" rel="noreferrer"
                    className="text-xs text-blue-600 hover:underline shrink-0">View</a>
                  <button onClick={() => setDeleteI983Id(doc.id)}
                    className="text-xs text-red-400 hover:text-red-600 shrink-0">🗑</button>
                </div>
                {deleteI983Id === doc.id && (
                  <div className="mt-1 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-2 text-xs">
                    <span className="text-red-700 flex-1">Delete this I-983?</span>
                    <button onClick={() => setDeleteI983Id(null)} disabled={deleteI983Loading}
                      className="border border-slate-300 text-slate-600 px-2 py-0.5 rounded hover:bg-white">Cancel</button>
                    <button onClick={() => deleteI983(doc.id)} disabled={deleteI983Loading}
                      className="bg-red-600 text-white px-2 py-0.5 rounded hover:bg-red-700 font-semibold">
                      {deleteI983Loading ? '…' : 'Delete'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <form onSubmit={uploadI983} className="space-y-2">
          <div
            className="border border-dashed border-violet-300 rounded-lg p-3 text-center cursor-pointer bg-violet-50/40 hover:bg-violet-50 transition-colors"
            onClick={() => i983Ref.current?.click()}>
            {i983File
              ? <span className="text-sm text-blue-600 font-medium">{i983File.name}</span>
              : <span className="text-sm text-slate-400">
                  {initialDocs.length > 0 ? 'Upload updated I-983…' : 'Click to upload I-983 document (PDF / JPG / PNG)'}
                </span>}
          </div>
          <input ref={i983Ref} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
            onChange={(e) => {
              const f = e.target.files[0] || null;
              if (f && f.size > 20 * 1024 * 1024) { setI983Err(`File too large (${(f.size/1024/1024).toFixed(1)} MB). Max 20 MB.`); e.target.value = ''; }
              else { setI983File(f); setI983Err(''); }
            }} />
          <input value={i983Notes} onChange={(e) => setI983Notes(e.target.value)}
            placeholder="Notes (optional)" className="input-base text-sm" />
          {i983Err     && <p className="text-red-600 text-xs bg-red-50 border border-red-200 px-3 py-1.5 rounded-lg">{i983Err}</p>}
          {i983Success && <p className="text-green-600 text-xs bg-green-50 border border-green-200 px-3 py-1.5 rounded-lg">{i983Success}</p>}
          <button type="submit" disabled={i983Loading || !i983File}
            className="w-full bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
            {i983Loading
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Uploading…</>
              : 'Upload I-983'}
          </button>
        </form>
      </div>

      {/* ── Complete approval section ── */}
      <div className="border-t border-violet-200">
        <button
          type="button"
          onClick={() => setShowApproveForm(!showApproveForm)}
          className="w-full px-5 py-3 flex items-center justify-between hover:bg-violet-100/50 transition-colors">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-violet-800 text-sm">Complete STEM OPT Approval</span>
            <span className="text-xs text-slate-400 font-normal">— enter after USCIS approves</span>
          </div>
          <span className="text-xs text-violet-600">{showApproveForm ? '▲' : '▼'}</span>
        </button>

        {showApproveForm && (
          <form onSubmit={handleApprove} className="px-5 pb-5 space-y-3 bg-white border-t border-violet-100">
            <p className="text-xs text-slate-500 pt-3">
              Enter details from the USCIS approval notice. OPT history is preserved automatically.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="STEM USCIS Number" required>
                <input value={approveForm.stem_uscis_number}
                  onChange={(e) => sf('stem_uscis_number', e.target.value)}
                  placeholder="STEM USCIS number" className="input-base" />
              </Field>
              <Field label="STEM OPT Start Date" required>
                <input type="date" value={approveForm.stem_start_date}
                  onChange={(e) => sf('stem_start_date', e.target.value)} className="input-base" />
              </Field>
              <Field label="STEM OPT Expiry Date" required>
                <input type="date" value={approveForm.stem_expiry_date}
                  onChange={(e) => sf('stem_expiry_date', e.target.value)} className="input-base" />
              </Field>
            </div>
            <Field label="Notes">
              <input value={approveForm.stem_notes}
                onChange={(e) => sf('stem_notes', e.target.value)}
                placeholder="Approval notes (optional)" className="input-base" />
            </Field>
            <p className="text-xs text-slate-400">After approval, upload the STEM OPT EAD card in the EAD Card tab below.</p>

            {approveErr && (
              <p className="text-red-600 text-sm bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{approveErr}</p>
            )}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setShowApproveForm(false)}
                className="flex-1 border border-slate-300 text-slate-600 py-2.5 rounded-lg text-sm hover:bg-slate-50">
                Cancel
              </button>
              <button type="submit" disabled={approveLoading}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
                {approveLoading
                  ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Processing…</>
                  : '✓ Approve STEM OPT'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   STEM Active Record (shown for fully approved STEM OPT employees)
══════════════════════════════════════════════════════════════════════════════ */
function StemActiveRecord({ employee, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    stem_uscis_number: '',
    stem_ead_number:   '',
    stem_start_date:   '',
    stem_expiry_date:  '',
    stem_notes:        '',
  });
  const [saving,      setSaving]      = useState(false);
  const [saveErr,     setSaveErr]     = useState('');
  const [stemDocFile, setStemDocFile] = useState(null);
  const stemDocRef = useRef();
  const sf = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  function openEdit() {
    setForm({
      stem_uscis_number: '',
      stem_ead_number:   '',
      stem_start_date:   '',
      stem_expiry_date:  '',
      stem_notes:        '',
    });
    setStemDocFile(null);
    setSaveErr('');
    setEditing(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaveErr('');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('employee_id',      String(employee.id));
      fd.append('stem_uscis_number', form.stem_uscis_number);
      fd.append('stem_ead_number',   form.stem_ead_number);
      fd.append('stem_start_date',   form.stem_start_date);
      fd.append('stem_expiry_date',  form.stem_expiry_date);
      fd.append('stem_notes',        form.stem_notes || '');
      if (stemDocFile) fd.append('stem_document', stemDocFile);
      await employeesAPI.updateStem(fd);
      setEditing(false);
      onSaved();
    } catch (ex) {
      setSaveErr(ex.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  const stemExpDays = employee.stem_expiry_date
    ? Math.floor((new Date(employee.stem_expiry_date) - new Date()) / 86400000)
    : null;
  const stemExpCls =
    stemExpDays === null  ? 'text-slate-600' :
    stemExpDays < 0       ? 'text-red-600 font-semibold' :
    stemExpDays <= 30     ? 'text-orange-600 font-semibold' :
    stemExpDays <= 90     ? 'text-yellow-700' :
                            'text-green-700';

  return (
    <div className="bg-white rounded-xl border border-violet-200 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 bg-violet-50 border-b border-violet-100 flex items-center gap-3">
        <span className="text-lg">🎓</span>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-violet-800">STEM OPT Immigration Records</h3>
            <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold">Active</span>
          </div>
          <p className="text-xs text-violet-500 mt-0.5">Both OPT and STEM OPT records preserved.</p>
        </div>
        <button
          onClick={editing ? () => setEditing(false) : openEdit}
          className={`shrink-0 text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${
            editing
              ? 'border-slate-300 text-slate-600 bg-white hover:bg-slate-50'
              : 'border-violet-300 text-violet-700 bg-white hover:bg-violet-50'
          }`}>
          {editing ? 'Cancel' : 'Edit Details'}
        </button>
      </div>

      {/* ── Edit form ── */}
      {editing ? (
        <form onSubmit={handleSave} className="p-5 space-y-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Update STEM OPT Details</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="STEM USCIS Number" required>
              <input value={form.stem_uscis_number} onChange={(e) => sf('stem_uscis_number', e.target.value)}
                placeholder="STEM USCIS number" className="input-base" required />
            </Field>
            <Field label="STEM EAD Card Number" required>
              <input value={form.stem_ead_number} onChange={(e) => sf('stem_ead_number', e.target.value)}
                placeholder="EAD card number" className="input-base" required />
            </Field>
            <Field label="STEM OPT Start Date" required>
              <input type="date" value={form.stem_start_date}
                onChange={(e) => sf('stem_start_date', e.target.value)} className="input-base" required />
            </Field>
            <Field label="STEM OPT Expiry Date" required>
              <input type="date" value={form.stem_expiry_date}
                onChange={(e) => sf('stem_expiry_date', e.target.value)} className="input-base" required />
            </Field>
          </div>
          <Field label="Notes">
            <input value={form.stem_notes} onChange={(e) => sf('stem_notes', e.target.value)}
              placeholder="Notes (optional)" className="input-base" />
          </Field>

          {/* STEM document attachment */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              STEM EAD / Approval Document
              <span className="text-slate-400 ml-1">(optional — PDF / JPG / PNG)</span>
            </label>
            {employee.stem_file_name && !stemDocFile && (
              <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-2">
                ✓ Current: {employee.stem_file_name} — select a new file to replace it.
              </p>
            )}
            <div
              className="border border-dashed border-slate-300 rounded-lg p-3 text-center cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors"
              onClick={() => stemDocRef.current?.click()}>
              {stemDocFile
                ? <span className="text-sm text-blue-600 font-medium">{stemDocFile.name}</span>
                : <span className="text-sm text-slate-400">Click to attach STEM EAD card or approval letter</span>}
            </div>
            <input ref={stemDocRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
              onChange={(e) => {
                const f = e.target.files[0] || null;
                if (f && f.size > 20 * 1024 * 1024) { setSaveErr(`File too large (${(f.size/1024/1024).toFixed(1)} MB). Max 20 MB.`); e.target.value = ''; }
                else { setStemDocFile(f); setSaveErr(''); }
              }} />
          </div>

          {saveErr && <p className="text-red-600 text-sm bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{saveErr}</p>}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={() => setEditing(false)}
              className="flex-1 border border-slate-300 text-slate-600 py-2.5 rounded-lg text-sm hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
              {saving
                ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</>
                : 'Save Details'}
            </button>
          </div>
        </form>
      ) : (
        /* ── Read-only view ── */
        <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
          {/* OPT Record */}
          <div className="p-5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />
              OPT Record
            </p>
            <div className="space-y-2.5">
              <RecordRow label="USCIS Number"    value={employee.uscis_number || '—'} />
              <RecordRow label="EAD Expiry (OPT)" value={fmtDate(employee.ead_expiry_date)} />
            </div>
          </div>

          {/* STEM OPT Record */}
          <div className="p-5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-violet-500 inline-block" />
              STEM OPT Record
            </p>
            <div className="space-y-2.5">
              <RecordRow label="STEM USCIS Number" value={employee.stem_uscis_number || '—'} />
              <RecordRow label="STEM EAD Number"   value={employee.stem_ead_number   || '—'} />
              <RecordRow label="Start Date"         value={fmtDate(employee.stem_start_date)} />
              <RecordRow
                label="Expiry Date"
                value={
                  <span className={stemExpCls}>
                    {fmtDate(employee.stem_expiry_date)}
                    {stemExpDays !== null && (
                      <span className="text-xs font-normal ml-1.5 opacity-80">
                        {stemExpDays < 0
                          ? `(expired ${Math.abs(stemExpDays)}d ago)`
                          : `(${stemExpDays}d left)`}
                      </span>
                    )}
                  </span>
                }
              />
              {employee.stem_notes && (
                <RecordRow label="Notes" value={<span className="text-slate-500 italic text-xs">{employee.stem_notes}</span>} />
              )}
              {employee.stem_file_path && (
                <RecordRow
                  label="Document"
                  value={
                    <a href={employeesAPI.stemFileUrl(employee.id)} target="_blank" rel="noreferrer"
                      className="text-xs text-blue-600 hover:underline font-medium">
                      {employee.stem_file_name || 'Preview'}
                    </a>
                  }
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RecordRow({ label, value }) {
  return (
    <div className="flex items-start gap-2 justify-between">
      <span className="text-xs text-slate-400 shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-slate-700 text-right">{value}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   I-983 Manager (shown for STEM OPT employees)
══════════════════════════════════════════════════════════════════════════════ */
function I983Manager({ employeeId, i983List, onUploaded }) {
  const [showForm,      setShowForm]      = useState(false);
  const [reason,        setReason]        = useState('rate_change');
  const [file,          setFile]          = useState(null);
  const [newExpiry,     setNewExpiry]     = useState('');
  const [vspSigned,     setVspSigned]     = useState('');
  const [notes,         setNotes]         = useState('');
  const [loading,       setLoading]       = useState(false);
  const [err,           setErr]           = useState('');
  const [success,       setSuccess]       = useState('');
  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editingId,     setEditingId]     = useState(null);
  const [editExpiry,    setEditExpiry]    = useState('');
  const [editVsp,       setEditVsp]       = useState('');
  const [editNotes,     setEditNotes]     = useState('');
  const [editLoading,   setEditLoading]   = useState(false);
  const [editErr,       setEditErr]       = useState('');
  const fileRef = useRef();

  function openEdit(doc) {
    setEditingId(doc.id);
    setEditExpiry(doc.new_expiry_date || '');
    setEditVsp(doc.vsp_signed_date || '');
    setEditNotes(doc.notes || '');
    setEditErr('');
  }

  function closeEdit() {
    setEditingId(null);
    setEditExpiry(''); setEditVsp(''); setEditNotes(''); setEditErr('');
  }

  async function handleI983Edit(e) {
    e.preventDefault();
    setEditLoading(true); setEditErr('');
    try {
      await i983API.update({ id: editingId, new_expiry_date: editExpiry, vsp_signed_date: editVsp, notes: editNotes });
      closeEdit();
      onUploaded();
    } catch (ex) {
      setEditErr(ex.response?.data?.message || 'Update failed.');
    } finally {
      setEditLoading(false);
    }
  }

  async function handleI983Delete() {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await i983API.delete(deleteTarget);
      setDeleteTarget(null);
      onUploaded();
    } catch (ex) {
      alert(ex.response?.data?.message || 'Delete failed.');
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleUpload(e) {
    e.preventDefault();
    setErr(''); setSuccess('');
    if (!file) { setErr('Please select an I-983 file.'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('employee_id',    String(employeeId));
      fd.append('reason',         reason);
      fd.append('i983_file',      file);
      if (newExpiry)  fd.append('new_expiry_date', newExpiry);
      if (vspSigned)  fd.append('vsp_signed_date', vspSigned);
      if (notes)      fd.append('notes', notes);
      await i983API.upload(fd);
      setSuccess('I-983 uploaded successfully!');
      setFile(null); setNewExpiry(''); setVspSigned(''); setNotes('');
      if (fileRef.current) fileRef.current.value = '';
      setTimeout(() => { setSuccess(''); setShowForm(false); onUploaded(); }, 1800);
    } catch (ex) {
      setErr(ex.response?.data?.message || 'Upload failed.');
    } finally {
      setLoading(false);
    }
  }

  const reasonLabel = (v) => I983_REASONS.find((r) => r.value === v)?.label || v;

  return (
    <div className="bg-white rounded-xl border border-violet-200 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 bg-violet-50 border-b border-violet-100">
        <div>
          <h3 className="font-semibold text-violet-800">I-983 Documents</h3>
          <p className="text-xs text-violet-600 mt-0.5">STEM OPT training plan records — {i983List.length} document{i983List.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => { setShowForm(!showForm); setErr(''); }}
          className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium">
          {showForm ? 'Cancel' : '+ Upload I-983'}
        </button>
      </div>

      {/* Upload form */}
      {showForm && (
        <form onSubmit={handleUpload} className="px-5 py-4 space-y-3 border-b border-slate-100 bg-slate-50/50">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Reason" required>
              <select value={reason} onChange={(e) => setReason(e.target.value)} className="input-base">
                {I983_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </Field>
            <Field label="Updated STEM OPT Expiry Date">
              <input type="date" value={newExpiry} onChange={(e) => setNewExpiry(e.target.value)} className="input-base" />
            </Field>
            <Field label="VSP Signed Date">
              <input type="date" value={vspSigned} onChange={(e) => setVspSigned(e.target.value)} className="input-base" />
            </Field>
          </div>

          <Field label="I-983 File" required>
            <div className="drop-zone rounded-lg p-3 text-center cursor-pointer" onClick={() => fileRef.current?.click()}>
              {file
                ? <p className="text-sm text-blue-600 font-medium">{file.name} <span className="text-slate-400 font-normal">({(file.size/1024/1024).toFixed(1)} MB)</span></p>
                : <p className="text-sm text-slate-400">Click to select I-983 document (max 20 MB)</p>}
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
              onChange={(e) => {
                const f = e.target.files[0] || null;
                if (f && f.size > 20 * 1024 * 1024) { setErr(`File too large (${(f.size/1024/1024).toFixed(1)} MB). Max 20 MB.`); e.target.value = ''; }
                else { setFile(f); setErr(''); }
              }} />
          </Field>

          <Field label="Notes">
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" className="input-base" />
          </Field>

          {err     && <p className="text-red-600 text-sm bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{err}</p>}
          {success && <p className="text-green-600 text-sm bg-green-50 border border-green-200 px-3 py-2 rounded-lg">{success}</p>}

          <button type="submit" disabled={loading}
            className="w-full bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
            {loading
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner"/>Uploading…</>
              : 'Upload I-983'}
          </button>
        </form>
      )}

      {/* I-983 history list */}
      {i983List.length === 0 ? (
        <div className="px-5 py-8 text-center text-slate-400 text-sm">No I-983 documents uploaded yet.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {i983List.map((doc) => (
            <div key={doc.id} className="px-5 py-3">
              <div className="flex flex-wrap items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-violet-600">v{doc.version}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-slate-800">
                      {doc.reason === 'initial_stem_opt' ? 'Initial STEM OPT' : reasonLabel(doc.reason)}
                    </span>
                    {doc.new_expiry_date && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
                        New expiry: {fmtDate(doc.new_expiry_date)}
                      </span>
                    )}
                    {doc.vsp_signed_date && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                        VSP signed: {fmtDate(doc.vsp_signed_date)}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">Uploaded {fmtDate(doc.created_at)}</p>
                  {doc.notes && <p className="text-xs text-slate-500 italic mt-0.5">{doc.notes}</p>}
                  <div className="flex flex-wrap gap-3 mt-1.5 items-center">
                    <a href={i983API.previewUrl(doc)} target="_blank" rel="noreferrer"
                      className="text-xs text-blue-600 hover:underline">Preview I-983</a>
                    {doc.offer_letter_path && (
                      <a href={i983API.offerLetterUrl(doc)} target="_blank" rel="noreferrer"
                        className="text-xs text-blue-600 hover:underline">Preview Offer Letter</a>
                    )}
                    <button onClick={() => editingId === doc.id ? closeEdit() : openEdit(doc)}
                      className="text-xs text-violet-500 hover:text-violet-700">
                      {editingId === doc.id ? 'Cancel Edit' : '✏ Edit'}
                    </button>
                    <button onClick={() => setDeleteTarget(doc.id)}
                      className="text-xs text-red-400 hover:text-red-600 ml-auto">
                      🗑 Delete
                    </button>
                  </div>
                </div>
              </div>

              {/* Inline edit form */}
              {editingId === doc.id && (
                <form onSubmit={handleI983Edit} className="mt-3 ml-11 space-y-3 bg-violet-50 border border-violet-200 rounded-lg p-4">
                  <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide">Edit I-983 Details</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Updated STEM OPT Expiry Date">
                      <input type="date" value={editExpiry} onChange={(e) => setEditExpiry(e.target.value)} className="input-base" />
                    </Field>
                    <Field label="VSP Signed Date">
                      <input type="date" value={editVsp} onChange={(e) => setEditVsp(e.target.value)} className="input-base" />
                    </Field>
                  </div>
                  <Field label="Notes">
                    <input value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Optional notes" className="input-base" />
                  </Field>
                  {editErr && <p className="text-red-600 text-xs bg-red-50 border border-red-200 px-3 py-1.5 rounded-lg">{editErr}</p>}
                  <div className="flex gap-2">
                    <button type="submit" disabled={editLoading}
                      className="bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white px-4 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1">
                      {editLoading ? <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full spinner"/>Saving…</> : 'Save Changes'}
                    </button>
                    <button type="button" onClick={closeEdit}
                      className="border border-slate-300 text-slate-600 px-4 py-1.5 rounded-lg text-xs hover:bg-white">
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          ))}
        </div>
      )}

      {/* I-983 delete confirmation */}
      {deleteTarget && (
        <div className="border-t border-red-100 bg-red-50 px-5 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <p className="text-sm text-red-700 flex-1">Permanently delete this I-983 record and its files?</p>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => setDeleteTarget(null)} disabled={deleteLoading}
              className="border border-slate-300 text-slate-600 px-3 py-1.5 rounded-lg text-xs hover:bg-white">
              Cancel
            </button>
            <button onClick={handleI983Delete} disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1">
              {deleteLoading ? <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full spinner"/>…</> : 'Yes, Delete'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Document card
══════════════════════════════════════════════════════════════════════════════ */
function DocCard({ employeeId, docType, config, grouped, employee, onSaved }) {
  const current    = grouped?.current  || null;
  const history    = grouped?.history  || [];
  const isUploaded = current?.status === 'uploaded' || !!current?.is_not_applicable;
  const isNA       = !!current?.is_not_applicable;
  const expStatus  = current?.expiry_status || 'no_expiry';

  const allDocs = [current, ...history].filter(Boolean).filter(d => d.status !== 'pending');
  const addLabel = allDocs.length === 0
    ? `Upload ${config.label}`
    : (employee?.category === 'STEM_OPT' && docType === 'ead_card')
      ? 'Add STEM OPT EAD'
      : `Add New ${config.label}`;

  const [showAddForm,    setShowAddForm]    = useState(false);
  const [formData,       setFormData]       = useState({});
  const [markNA,         setMarkNA]         = useState(false);
  const [file,           setFile]           = useState(null);
  const [stemUscis,      setStemUscis]      = useState('');
  const [loading,        setLoading]        = useState(false);
  const [success,        setSuccess]        = useState('');
  const [formErrors,     setFormErrors]     = useState({});
  const [saveError,      setSaveError]      = useState('');
  const [deleteTarget,   setDeleteTarget]   = useState(null);
  const [deleteLoading,  setDeleteLoading]  = useState(false);
  const [editingId,      setEditingId]      = useState(null);
  const [detailForm,     setDetailForm]     = useState({});
  const [detailLoading,  setDetailLoading]  = useState(false);
  const [detailError,    setDetailError]    = useState('');
  const [detailSuccess,  setDetailSuccess]  = useState('');
  const fileRef = useRef();

  function openAddForm() {
    const init = {};
    config.fields.forEach((f) => { init[f.key] = ''; });
    // Pre-populate expiry date for STEM OPT employees adding a new EAD card
    if (docType === 'ead_card' && employee?.category === 'STEM_OPT' && employee?.stem_expiry_date) {
      init.expiry_date = employee.stem_expiry_date;
    }
    setFormData(init);
    setStemUscis('');
    setFile(null); setMarkNA(false); setFormErrors({}); setSaveError(''); setSuccess('');
    setShowAddForm(true);
  }

  function openEditDetails(doc) {
    const init = {};
    config.fields.forEach((f) => {
      if (f.key === 'expiry_date')     init.expiry_date     = doc.expiry_date     || '';
      else if (f.key === 'start_date') init.start_date      = doc.start_date      || '';
      else                             init.document_number = doc.document_number || '';
    });
    setDetailForm(init);
    setDetailError(''); setDetailSuccess('');
    setEditingId(doc.id);
  }

  async function saveDetails(e) {
    e.preventDefault();
    setDetailError(''); setDetailSuccess('');
    setDetailLoading(true);
    try {
      await documentsAPI.updateDetails({ id: editingId, ...detailForm });
      setDetailSuccess('Saved!');
      const res = await documentsAPI.list(employeeId);
      onSaved(res.data.data.documents[docType] || { current: null, history: [] });
      setTimeout(() => { setDetailSuccess(''); setEditingId(null); }, 1400);
    } catch (err) {
      setDetailError(err.response?.data?.message || 'Update failed.');
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await documentsAPI.delete(deleteTarget.id);
      const res = await documentsAPI.list(employeeId);
      onSaved(res.data.data.documents[docType] || { current: null, history: [] });
      setDeleteTarget(null);
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed.');
    } finally {
      setDeleteLoading(false);
    }
  }

  function validate() {
    if (markNA) return {};
    const errs = {};
    config.fields.forEach((f) => {
      if (f.required && !String(formData[f.key] || '').trim()) errs[f.key] = `${f.label} is required.`;
      if (docType === 'ssn_card' && f.key === 'document_number' && !/^\d{4}$/.test(formData[f.key] || ''))
        errs[f.key] = 'Enter exactly the last 4 digits.';
    });
    if (config.hasFile && !file && !markNA)
      errs['file'] = `${config.fileLabel || 'Document file'} is required.`;
    if (docType === 'ead_card' && employee?.category === 'STEM_OPT' && allDocs.length > 0 && !stemUscis.trim())
      errs['stem_uscis'] = 'STEM USCIS Number is required.';
    return errs;
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaveError(''); setSuccess('');
    if (!employeeId || employeeId <= 0) {
      setSaveError('Invalid employee ID. Please refresh the page and try again.');
      return;
    }
    const errs = validate();
    if (Object.keys(errs).length) { setFormErrors(errs); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('employee_id',       String(employeeId));
      fd.append('document_type',     docType);
      fd.append('is_not_applicable', markNA ? '1' : '0');
      if (!markNA) {
        config.fields.forEach((f) => {
          if (f.key === 'expiry_date')          fd.append('expiry_date',     formData['expiry_date']     || '');
          else if (f.key === 'start_date')       fd.append('start_date',      formData['start_date']      || '');
          else if (f.key === 'document_number')  fd.append('document_number', formData['document_number'] || '');
        });
        if (file) fd.append('document_file', file);
        if (docType === 'ead_card' && stemUscis) fd.append('stem_uscis_number', stemUscis);
      }
      await documentsAPI.upload(fd);
      const res = await documentsAPI.list(employeeId);
      onSaved(res.data.data.documents[docType] || { current: null, history: [] });
      setSuccess('Saved!');
      setFile(null); setStemUscis(''); setFormErrors({});
      if (fileRef.current) fileRef.current.value = '';
      setTimeout(() => { setSuccess(''); setShowAddForm(false); }, 1800);
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Save failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  let border = 'border-slate-200';
  let sIcon = '⏳', sText = 'Pending', sCls = 'bg-slate-100 text-slate-500';
  if (isNA) {
    border='border-slate-300'; sIcon='N/A'; sText='Not Applicable'; sCls='bg-slate-100 text-slate-600';
  } else if (isUploaded) {
    if (!config.noAlert && expStatus==='expired')       { border='border-red-300';    sIcon='✗'; sText='Expired';       sCls='bg-red-100 text-red-700'; }
    else if (!config.noAlert && expStatus==='critical') { border='border-orange-300'; sIcon='⚠'; sText='Expiring Soon'; sCls='bg-orange-100 text-orange-700'; }
    else if (!config.noAlert && expStatus==='warning')  { border='border-yellow-300'; sIcon='⚠'; sText='60 days';      sCls='bg-yellow-100 text-yellow-700'; }
    else                                                 { border='border-green-300';  sIcon='✓'; sText='Valid';        sCls='bg-green-100 text-green-700'; }
  }

  return (
    <div className={`bg-white rounded-xl border-2 ${border} overflow-hidden`}>

      {/* ── Card header — static ── */}
      <div className="flex items-center gap-3 px-4 py-3.5">
        <span className="text-xl shrink-0">{config.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-800 text-sm">{config.label}</p>
          {allDocs.length > 0 && (
            <p className="text-xs text-slate-400">{allDocs.length} entr{allDocs.length === 1 ? 'y' : 'ies'}</p>
          )}
        </div>
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold shrink-0 ${sCls}`}>{sIcon} {sText}</span>
      </div>

      {/* ── All document entries — newest first, always visible ── */}
      {allDocs.length > 0 && (
        <div className="border-t border-slate-100 divide-y divide-slate-100">
          {allDocs.map((doc, idx) => {
            const isLatest = doc.id === current?.id;
            const isOldest = idx === allDocs.length - 1;
            const docExp = doc.expiry_status || (
              doc.expiry_date && !config.noAlert
                ? (() => {
                    const d = Math.floor((new Date(doc.expiry_date) - new Date()) / 86400000);
                    return d < 0 ? 'expired' : d <= 30 ? 'critical' : d <= 60 ? 'warning' : 'valid';
                  })()
                : 'no_expiry'
            );
            const entryBadgeCls = isLatest
              ? (docExp === 'expired' ? 'bg-red-100 text-red-700' : docExp === 'critical' ? 'bg-orange-100 text-orange-700' : docExp === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700')
              : 'bg-slate-100 text-slate-500';

            // For STEM OPT employees with multiple entries, label oldest as OPT and newest as STEM OPT
            const isStemOpt = employee?.category === 'STEM_OPT';
            const entryLabel = isStemOpt && allDocs.length > 1
              ? (isLatest ? 'STEM OPT' : isOldest ? 'OPT' : `Previous v${doc.version}`)
              : (isLatest ? 'Latest' : `Previous v${doc.version}`);

            return (
              <div key={doc.id}>
                <div className="px-4 py-3 flex flex-wrap items-start gap-x-3 gap-y-1.5">
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${entryBadgeCls}`}>
                        {entryLabel}
                      </span>
                      <span className="text-xs text-slate-400">{fmtDate(doc.created_at)}</span>
                      {!config.noAlert && doc.expiry_date && docType !== 'company_joining' && (
                        <span className={`text-xs font-medium ${docExp === 'expired' ? 'text-red-600' : docExp === 'critical' ? 'text-orange-500' : docExp === 'warning' ? 'text-yellow-700' : 'text-green-600'}`}>
                          {docExp === 'expired' ? '✗ Expired' : docExp === 'critical' ? '⚠ Expiring soon' : docExp === 'warning' ? '⚠ 60 days' : '✓ Valid'}
                        </span>
                      )}
                    </div>
                    {!doc.is_not_applicable && doc.document_number && (
                      <p className="text-xs text-slate-600">{docType === 'ssn_card' ? `SSN: ****${doc.document_number}` : doc.document_number}</p>
                    )}
                    {docType === 'ead_card' && doc.start_date && (
                      <p className="text-xs text-slate-500">{fmtDate(doc.start_date)} → {fmtDate(doc.expiry_date)}</p>
                    )}
                    {!config.noAlert && doc.expiry_date && docType !== 'ead_card' && docType !== 'company_joining' && (
                      <p className="text-xs text-slate-500">Expires: {fmtDate(doc.expiry_date)}</p>
                    )}
                    {docType === 'company_joining' && doc.expiry_date && (
                      <p className="text-xs text-slate-500">Joined: {fmtDate(doc.expiry_date)}</p>
                    )}
                    {doc.file_name && <p className="text-xs text-slate-400 truncate max-w-xs">{doc.file_name}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap shrink-0">
                    {doc.file_path && (
                      <a href={documentsAPI.previewUrl(doc.id)} target="_blank" rel="noreferrer"
                        className="text-xs text-blue-600 hover:underline font-medium">Preview</a>
                    )}
                    {doc.file_path && (
                      <a href={documentsAPI.downloadUrl(doc.id)} target="_blank" rel="noreferrer"
                        className="text-xs text-blue-600 hover:underline">Download</a>
                    )}
                    {config.fields.length > 0 && !doc.is_not_applicable && (
                      <button
                        onClick={() => editingId === doc.id ? setEditingId(null) : openEditDetails(doc)}
                        className={`text-xs border px-2 py-0.5 rounded font-medium ${editingId === doc.id ? 'bg-slate-100 border-slate-300 text-slate-600' : 'text-blue-600 border-blue-200 hover:bg-blue-50'}`}>
                        {editingId === doc.id ? 'Cancel' : 'Edit'}
                      </button>
                    )}
                    <button onClick={() => setDeleteTarget({ id: doc.id, label: `v${doc.version}` })}
                      className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 border border-red-200 px-2 py-0.5 rounded font-medium">
                      Delete
                    </button>
                  </div>
                </div>

                {/* Inline details editor for this entry */}
                {editingId === doc.id && (
                  <form onSubmit={saveDetails} className="border-t border-blue-100 bg-blue-50/30 px-4 py-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Edit Details</p>
                      <button type="button" onClick={() => setEditingId(null)} className="text-slate-400 hover:text-slate-600 text-xs">✕</button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {config.fields.map((f) => (
                        <div key={f.key}>
                          <label className="block text-xs font-medium text-slate-600 mb-1">{f.label}</label>
                          <input type={f.type} value={detailForm[f.key] || ''}
                            onChange={(e) => {
                              let v = e.target.value;
                              if (docType === 'ssn_card' && f.key === 'document_number') v = v.replace(/\D/g,'').slice(0,4);
                              setDetailForm((p) => ({ ...p, [f.key]: v }));
                            }}
                            placeholder={f.placeholder || ''} maxLength={f.maxLength}
                            className="w-full border border-slate-300 bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                      ))}
                    </div>
                    {detailError   && <p className="text-red-600 text-xs bg-red-50 border border-red-200 px-3 py-1.5 rounded-lg">{detailError}</p>}
                    {detailSuccess && <p className="text-green-600 text-xs bg-green-50 border border-green-200 px-3 py-1.5 rounded-lg">{detailSuccess}</p>}
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setEditingId(null)}
                        className="flex-1 border border-slate-300 text-slate-600 py-2 rounded-lg text-sm hover:bg-white">Cancel</button>
                      <button type="submit" disabled={detailLoading}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
                        {detailLoading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner"/>Saving…</> : 'Save Details'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── STEM OPT EAD prompt — shown when employee is STEM_OPT but only has 1 EAD (OPT one) ── */}
      {docType === 'ead_card' && employee?.category === 'STEM_OPT' && allDocs.length === 1 && !showAddForm && (
        <div className="border-t border-violet-100 bg-violet-50 px-4 py-3 flex items-start gap-2">
          <span className="text-violet-500 text-sm mt-0.5">🎓</span>
          <p className="text-xs text-violet-700 flex-1">
            STEM OPT approved — please upload your new STEM OPT EAD card below.
          </p>
        </div>
      )}

      {/* ── Add New — always visible at bottom ── */}
      <div className="border-t border-slate-100">
        {!showAddForm ? (
          <button onClick={openAddForm}
            className="w-full px-4 py-2.5 flex items-center gap-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors font-medium">
            <span className="text-base leading-none">+</span>
            {addLabel}
          </button>
        ) : (
          <form onSubmit={handleSave} className="px-4 py-4 space-y-3 bg-slate-50/50">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                {addLabel}
              </p>
              <button type="button" onClick={() => setShowAddForm(false)} className="text-slate-400 hover:text-slate-600 text-xs">✕ Cancel</button>
            </div>

            {config.supportsNA && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={markNA} onChange={(e) => { setMarkNA(e.target.checked); setFormErrors({}); }} className="w-4 h-4 accent-blue-600" />
                <span className="text-sm text-slate-700">Mark as <strong>Not Applicable</strong></span>
              </label>
            )}

            {!markNA && (
              <>
                {docType === 'ead_card' && (
                  employee?.category === 'STEM_OPT' && allDocs.length > 0 ? (
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        STEM USCIS Number <span className="text-red-500">*</span>
                      </label>
                      <input value={stemUscis} onChange={(e) => { setStemUscis(e.target.value); setFormErrors((p) => ({ ...p, stem_uscis: '' })); }}
                        placeholder="STEM USCIS number"
                        className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.stem_uscis ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white'}`} />
                      {formErrors.stem_uscis && <p className="text-red-500 text-xs mt-0.5">{formErrors.stem_uscis}</p>}
                    </div>
                  ) : employee?.uscis_number ? (
                    <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm">
                      <span className="text-blue-500">🪪</span>
                      <span className="text-slate-500 text-xs">USCIS Number</span>
                      <span className="font-semibold text-slate-800 ml-1">{employee.uscis_number}</span>
                      <span className="text-xs text-blue-500 ml-auto">From profile</span>
                    </div>
                  ) : null
                )}
                {config.fields.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {config.fields.map((f) => (
                      <div key={f.key}>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          {f.label} {f.required && <span className="text-red-500">*</span>}
                        </label>
                        <input type={f.type} value={formData[f.key] || ''}
                          onChange={(e) => {
                            let v = e.target.value;
                            if (docType === 'ssn_card' && f.key === 'document_number') v = v.replace(/\D/g,'').slice(0,4);
                            setFormData((p) => ({ ...p, [f.key]: v }));
                            setFormErrors((p) => ({ ...p, [f.key]: '' }));
                          }}
                          placeholder={f.placeholder || ''} maxLength={f.maxLength}
                          className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors[f.key] ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white'}`} />
                        {formErrors[f.key] && <p className="text-red-500 text-xs mt-0.5">{formErrors[f.key]}</p>}
                      </div>
                    ))}
                  </div>
                )}
                {config.hasFile && (
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      {config.fileLabel || 'Document File'} <span className="text-red-500">*</span>
                      <span className="text-slate-400 ml-1">(PDF / JPG / PNG, max 20 MB)</span>
                    </label>
                    <div className="drop-zone rounded-lg p-3 text-center cursor-pointer" onClick={() => fileRef.current?.click()}>
                      {file
                        ? <p className="text-sm text-blue-600 font-medium">{file.name} <span className="text-slate-400 font-normal">({(file.size / 1024 / 1024).toFixed(1)} MB)</span></p>
                        : <p className="text-sm text-slate-400">Click to select or drag & drop</p>}
                    </div>
                    <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                      onChange={(e) => {
                        const f = e.target.files[0] || null;
                        if (f && f.size > 20 * 1024 * 1024) {
                          setFormErrors((p) => ({ ...p, file: `File is too large (${(f.size / 1024 / 1024).toFixed(1)} MB). Maximum allowed size is 20 MB.` }));
                          setFile(null);
                          e.target.value = '';
                        } else {
                          setFile(f);
                          setFormErrors((p) => ({ ...p, file: '' }));
                        }
                      }} />
                    {formErrors.file && <p className="text-red-500 text-xs mt-1">{formErrors.file}</p>}
                  </div>
                )}
              </>
            )}

            {saveError && <p className="text-red-600 text-sm bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{saveError}</p>}
            {success   && <p className="text-green-600 text-sm bg-green-50 border border-green-200 px-3 py-2 rounded-lg">{success}</p>}
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setShowAddForm(false)}
                className="flex-1 border border-slate-300 text-slate-600 py-2 rounded-lg text-sm hover:bg-slate-100">Cancel</button>
              <button type="submit" disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
                {loading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner"/>Saving…</> : 'Save Document'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="border-t border-red-100 bg-red-50 px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <p className="text-sm text-red-700 flex-1">Delete <strong>{deleteTarget.label}</strong>? The file will be permanently removed.</p>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => setDeleteTarget(null)} disabled={deleteLoading}
              className="border border-slate-300 text-slate-600 px-3 py-1.5 rounded-lg text-xs hover:bg-white">Cancel</button>
            <button onClick={handleDelete} disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1">
              {deleteLoading ? <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full spinner"/>…</> : 'Yes, Delete'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Helpers ─────────────────────────────────────────────────────────────────── */
function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

function Info({ icon, v }) {
  return (
    <div className="flex items-start gap-1.5 text-sm">
      <span className="shrink-0">{icon}</span>
      <span className="break-all">{v}</span>
    </div>
  );
}
